import { Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  athenaApi,
  HandlersLibraryTreeNode,
  useDeleteApiAttachmentsByIdMutation,
  useDeleteApiDeliverablesByIdMutation,
  useDeleteApiLibraryDirectoriesByDirectoryIdMutation,
  useDeleteApiProjectsByIdMutation,
} from '../../../../../app/athenaApiGen';
import {
  setInfoBarDetail,
  setSelectedItem,
  toggleModal,
} from '../../../../../app/library/librarySlice';
import { RootState, store } from '../../../../../app/store';
import DeleteConfirmation from '../../../../../components/Modals/DeleteConfirmation';

const DeleteConfirmationModal = () => {
  const dispatch = useDispatch();
  const [deleteAttachment, { isLoading: deletingAttachment }] =
    useDeleteApiAttachmentsByIdMutation();
  const [deleteDirectory, { isLoading: deletingDirectory }] =
    useDeleteApiLibraryDirectoriesByDirectoryIdMutation();
  const [deleteDeliverable, { isLoading: deletingDeliverable }] =
    useDeleteApiDeliverablesByIdMutation();
  const [deleteProject, { isLoading: deletingProject }] =
    useDeleteApiProjectsByIdMutation();
  const { selectedItem, modals } = useSelector(
    (state: RootState) => state.library,
  );

  // Client-side cascade for folder delete (issue #1328). The BE's
  // delete-directory endpoint doesn't cascade — children get re-parented
  // to the grandparent and silently survive, despite the dialog promising
  // "permanently removed, along with any folders and files inside it."
  // We walk the cached tree node depth-first and delete leaves first.
  // Failures inside the recursion are swallowed and reported via a
  // post-walk failure count on the outer handler; partial-failure is
  // possible because this is N round-trips, not one BE transaction.
  // The proper long-term fix is BE-side cascade — tracked separately.
  const cascadeDelete = async (
    node: HandlersLibraryTreeNode,
    failures: { count: number },
  ): Promise<void> => {
    for (const child of node.children ?? []) {
      try {
        await cascadeDelete(child, failures);
      } catch {
        // cascadeDelete already increments failures internally; the catch
        // here just prevents one branch's failure from aborting siblings.
      }
    }
    const t = node.entityType;
    const id = node.id ?? '';
    try {
      if (t === 'attachment') {
        await deleteAttachment({ id }).unwrap();
      } else if (t === 'deliverable') {
        // Deliverable delete uses its source-table UUID — same as the
        // tree node id per HandlersLibraryTreeNode docs.
        await deleteDeliverable({ id: node.entityId || id }).unwrap();
      } else if (t === 'library_directory') {
        await deleteDirectory({ directoryId: id }).unwrap();
      }
      // Programs / projects are not expected as descendants of a folder.
      // If one shows up, leave it alone rather than cascade-delete a
      // higher-level entity.
    } catch (err) {
      failures.count += 1;
      console.error('[DeleteConfirmationModal] cascade child delete failed', {
        entityType: t,
        id,
        err,
      });
    }
  };

  const handleDelete = async () => {
    // Capture identifiers before any state changes so we can still route to
    // the right backend even if Redux state moves under us mid-await.
    const entityType = selectedItem?.entityType;
    const itemId = selectedItem?.id ?? '';
    const entityId = selectedItem?.entityId ?? '';
    const label = selectedItem?.label ?? 'item';
    const childCount = selectedItem?.children?.length ?? 0;
    const failures = { count: 0 };

    // Optimistic-remove from the tree cache so the row disappears the moment
    // the user confirms Delete, instead of lingering until invalidateTags
    // round-trips. patchTree.undo() restores the node if the mutation fails.
    const removeFromTreeDraft = (
      nodes: HandlersLibraryTreeNode[] | undefined,
      targetId: string,
    ): boolean => {
      if (!nodes) return false;
      const idx = nodes.findIndex((n) => n.id === targetId);
      if (idx !== -1) {
        nodes.splice(idx, 1);
        return true;
      }
      for (const n of nodes) {
        if (removeFromTreeDraft(n.children, targetId)) return true;
      }
      return false;
    };
    const patchTree = store.dispatch(
      athenaApi.util.updateQueryData(
        'getApiLibraryTree',
        { includeAll: false },
        (draft) => {
          if (itemId) removeFromTreeDraft(draft, itemId);
        },
      ) as unknown as Parameters<typeof store.dispatch>[0],
    ) as unknown as { undo: () => void };

    try {
      if (entityType === 'attachment') {
        await deleteAttachment({ id: itemId }).unwrap();
      } else if (entityType === 'project') {
        // Project uses its own UUID (entityId) for the delete endpoint —
        // the library tree id and the source-table id are the same per
        // HandlersLibraryTreeNode docs ("Original UUID from source table").
        await deleteProject({ id: entityId || itemId }).unwrap();
      } else if (entityType === 'library_directory') {
        // Walk descendants first (BE doesn't cascade — issue #1328),
        // then delete the folder itself. Children come from the cached
        // tree node, which the panel already has loaded.
        if (selectedItem) {
          for (const child of selectedItem.children ?? []) {
            await cascadeDelete(child, failures);
          }
        }
        await deleteDirectory({ directoryId: itemId }).unwrap();
      } else {
        // Falls through for unknown folder-like types.
        await deleteDirectory({ directoryId: itemId }).unwrap();
      }
      // Only clear selection + close modal AFTER a successful delete.
      // Doing it earlier left the modal in a degraded state ("Delete this
      // item?") if the delete failed, and a second click would fall through
      // to deleteDirectory with an empty id.
      dispatch(setSelectedItem(null));
      dispatch(toggleModal('deleteConfirmation'));
      dispatch(athenaApi.util.invalidateTags(['Library']));
      // Surface partial-failure on cascade so the user knows the tree
      // may not be fully cleaned up (rare, but possible since cascade
      // is N independent BE calls until the BE owns this transaction).
      if (failures.count > 0 && childCount > 0) {
        dispatch(
          setInfoBarDetail(
            `${label} deleted, but ${failures.count} item${
              failures.count === 1 ? '' : 's'
            } inside it could not be removed. Refresh to see what remains.`,
          ),
        );
      }
    } catch (err: any) {
      // Restore the optimistic-removed node so the user sees the row
      // come back when the delete fails instead of being silently
      // misled into thinking it worked.
      patchTree.undo();
      // Surface the failure instead of silently swallowing it. Common
      // failure modes: 403 (lacking archive-project / archive-directory
      // permission), 404 (already archived), 500 (server issue).
      const status = err?.status;
      const message =
        err?.data?.details ||
        err?.data?.message ||
        (status === 403
          ? `You don't have permission to delete ${label}.`
          : status === 404
            ? `${label} was not found (it may already be deleted).`
            : `Failed to delete ${label}. Please try again.`);
      console.error('[DeleteConfirmationModal] delete failed', { status, err });
      dispatch(setInfoBarDetail(message));
    }
  };

  // Entity-aware title + body. Cascade callout for folders/projects since
  // deleting them takes everything underneath. File case doesn't cascade
  // (an attachment is a leaf — no children to lose).
  const entityType = selectedItem?.entityType;
  const label = selectedItem?.label ?? 'this item';
  const noun =
    entityType === 'attachment'
      ? 'file'
      : entityType === 'project'
        ? 'project'
        : entityType === 'library_directory'
          ? 'folder'
          : 'item';
  const title = `Delete this ${noun}?`;
  const cascade =
    noun === 'folder'
      ? ', along with any folders and files inside it'
      : noun === 'project'
        ? ', along with all folders, deliverables, and files inside it'
        : '';

  return (
    <DeleteConfirmation
      open={modals.deleteConfirmation.open}
      title={title}
      warning
      onClose={() => dispatch(toggleModal('deleteConfirmation'))}
      body={
        <>
          <Typography variant="body2">
            <strong>{label}</strong> will be permanently removed{cascade}.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
            This action cannot be undone.
          </Typography>
        </>
      }
      handleConfirmation={handleDelete}
      isLoading={
        deletingAttachment ||
        deletingDirectory ||
        deletingDeliverable ||
        deletingProject
      }
    />
  );
};

export default DeleteConfirmationModal;
